import { useState, useEffect } from 'react';
import axios from 'axios';
import { Wrench, Clock, Check } from 'lucide-react';
import Layout from '@/components/layout/Layout';
import BookingZone from '@/components/BookingZone';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const InterventionPage = () => {
  const [pageSettings, setPageSettings] = useState(null);
  const [services, setServices] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [pageRes, servicesRes] = await Promise.all([
          axios.get(`${API}/pages/intervention`),
          axios.get(`${API}/services`)
        ]);
        setPageSettings(pageRes.data);
        setServices(servicesRes.data);
      } catch (error) {
        console.error('Error fetching data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const advantages = [
    "Intervention rapide sous 24-48h",
    "Devis gratuit et sans engagement",
    "Équipe qualifiée et équipée",
    "Nettoyage du chantier inclus",
    "Facture détaillée"
  ];

  if (loading) {
    return (
      <Layout>
        <div className="min-h-screen flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-4 border-emerald-600 border-t-transparent"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      {/* Hero */}
      <section className="bg-gradient-to-b from-teal-50 to-white py-16 lg:py-24 pt-32 lg:pt-40" data-testid="intervention-hero">
        <div className="container-custom">
          <div className="max-w-3xl mx-auto text-center">
            <span className="inline-block bg-teal-100 text-teal-700 text-sm font-medium px-4 py-2 rounded-full mb-6">
              <Wrench className="w-4 h-4 inline mr-2" />
              Intervention ponctuelle
            </span>
            <h1 className="font-heading font-bold text-4xl md:text-5xl text-stone-900 mb-6">
              {pageSettings?.title || "Demander une intervention ponctuelle"}
            </h1>
            <p className="text-stone-600 text-lg">
              {pageSettings?.description || "Besoin d'une intervention rapide ? Réservez un créneau pour une prestation unique."}
            </p>
          </div>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-8 lg:py-16" data-testid="intervention-content">
        <div className="container-custom">
          {/* Mobile: Formulaire en premier, Desktop: côte à côte */}
          <div className="flex flex-col lg:grid lg:grid-cols-2 gap-8 lg:gap-12">
            
            {/* Booking Zone - Appears FIRST on mobile, RIGHT on desktop */}
            <div className="lg:order-2 bg-white rounded-3xl shadow-xl border border-stone-100 p-4 sm:p-6 lg:p-8 lg:sticky lg:top-24 lg:self-start">
              <h2 className="font-heading font-bold text-xl sm:text-2xl text-stone-900 mb-4 sm:mb-6 text-center">
                Demander une intervention
              </h2>
              <div className="iframe-container">
                <BookingZone 
                  embedHtml={pageSettings?.embed_html} 
                  placeholder="Le formulaire de demande d'intervention sera affiché ici"
                />
              </div>
            </div>

            {/* Info Section - Appears SECOND on mobile, LEFT on desktop */}
            <div className="lg:order-1">
              <h2 className="font-heading font-bold text-2xl text-stone-900 mb-6">
                Intervention rapide et efficace
              </h2>
              
              <div className="bg-emerald-50 rounded-2xl p-6 mb-8">
                <div className="flex items-center gap-4 mb-4">
                  <div className="w-12 h-12 bg-emerald-600 rounded-xl flex items-center justify-center">
                    <Clock className="w-6 h-6 text-white" />
                  </div>
                  <div>
                    <p className="font-heading font-semibold text-stone-900">Délai d'intervention</p>
                    <p className="text-emerald-600 font-bold text-xl">24-48 heures</p>
                  </div>
                </div>
                <p className="text-stone-600 text-sm">
                  Nous nous engageons à intervenir rapidement pour répondre à vos besoins urgents.
                </p>
              </div>

              <ul className="space-y-4 mb-8">
                {advantages.map((advantage, index) => (
                  <li key={index} className="flex items-start gap-3">
                    <div className="flex-shrink-0 w-6 h-6 bg-teal-100 rounded-full flex items-center justify-center mt-0.5">
                      <Check className="w-4 h-4 text-teal-600" />
                    </div>
                    <span className="text-stone-700">{advantage}</span>
                  </li>
                ))}
              </ul>

              <h3 className="font-heading font-semibold text-xl text-stone-900 mb-4">
                Types d'interventions
              </h3>
              <div className="grid grid-cols-1 gap-3">
                {services.slice(0, 6).map((service) => (
                  <div 
                    key={service.id}
                    className="bg-white rounded-xl p-4 border border-stone-200 flex items-center gap-4"
                  >
                    <img 
                      src={service.image_url} 
                      alt={service.title}
                      className="w-16 h-16 rounded-lg object-cover"
                    />
                    <div>
                      <p className="font-medium text-stone-900">{service.title}</p>
                      <p className="text-stone-500 text-sm line-clamp-1">{service.description}</p>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-16 bg-teal-600" data-testid="intervention-cta">
        <div className="container-custom text-center">
          <h2 className="font-heading font-bold text-2xl text-white mb-4">
            Besoin d'une intervention urgente ?
          </h2>
          <p className="text-teal-100 mb-6">
            Appelez-nous directement pour une prise en charge immédiate.
          </p>
          <a 
            href="tel:0465011022" 
            className="inline-flex items-center gap-2 bg-white text-teal-600 font-semibold px-8 py-4 rounded-full hover:bg-stone-100 transition-colors text-lg"
          >
            04 65 01 10 22
          </a>
        </div>
      </section>
    </Layout>
  );
};

export default InterventionPage;
